import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ProductImageThumb } from "@/components/catalog/ProductImageThumb";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { SalePaymentLinesSection } from "@/components/sales/SalePaymentLinesSection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import { resolveProductThumbnailUrl } from "@/lib/catalog/productThumbnail";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import {
  fetchSaleReturnInvoice,
  saleInvoiceErrorMessage,
  type SaleInvoiceRecord,
  type SalePaymentStatus,
} from "@/lib/sales/saleInvoiceStore";
import {
  buildSalePaymentsPayload,
  newSalePaymentLine,
  salePaymentLinesTotal,
  type SalePaymentLine,
} from "@/lib/sales/salePaymentLines";
import { processSaleReturn } from "@/lib/sales/saleReturnsStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { format, isValid, parseISO } from "date-fns";
import {
  ArrowLeft,
  Car,
  Download,
  ExternalLink,
  FileText,
  History,
  Loader2,
  Phone,
  RotateCcw,
  Undo2,
  User,
  UserCheck,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

/* ─── Formatting helpers ──────────────────────────────────────────── */

function formatAmount(n: number): string {
  return n.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function MoneyAmount({
  value,
  className,
  amountClassName,
  emphasis = false,
  showShort = false,
}: {
  value: number | null | undefined;
  className?: string;
  amountClassName?: string;
  emphasis?: boolean;
  showShort?: boolean;
}) {
  if (value == null || !Number.isFinite(Number(value))) {
    return <span className={cn("text-muted-foreground", className)}>—</span>;
  }

  const num = Number(value);
  const short = showShort ? pkrAmountShortLabel(num) : "";

  return (
    <span className={cn("inline-flex flex-col items-end tabular-nums", className)}>
      <span className={cn(emphasis ? "font-semibold text-foreground" : "font-medium text-foreground", amountClassName)}>
        {formatCurrency(num)}
      </span>
      {short ? <span className="text-[10px] text-muted-foreground">{short}</span> : null}
    </span>
  );
}

function invoiceLabel(sale: SaleInvoiceRecord): string {
  return sale.invoice_no || String(sale.id);
}

function PaymentStatusBadge({ status }: { status: SalePaymentStatus | null | undefined }) {
  if (!status) return null;
  const label =
    status === "paid"
      ? "Fully paid"
      : status === "partial"
        ? "Partially paid"
        : status === "returned"
          ? "Returned"
          : status === "cancelled"
            ? "Cancelled"
            : "Unpaid";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
        status === "paid"
          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30"
          : status === "partial"
            ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30"
            : status === "returned"
              ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/30"
              : status === "cancelled"
                ? "bg-slate-500/15 text-slate-700 dark:text-slate-300 border border-slate-500/30"
                : "bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/30",
      )}
    >
      {status === "paid" ? <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> : null}
      {label}
    </span>
  );
}

function vehicleStatusBadge(sale: SaleInvoiceRecord): { label: string; className: string } {
  if (sale.is_returned || sale.void_type === "return" || sale.payment_status === "returned") {
    return {
      label: "Returned",
      className: "bg-violet-500/15 text-violet-700 dark:text-violet-300",
    };
  }
  if (sale.is_cancelled || sale.cancelled_at || sale.payment_status === "cancelled") {
    return {
      label: "Cancelled",
      className: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
    };
  }

  const status = (sale.product?.inventory_status ?? "sold").toLowerCase();
  if (status === "in_stock") {
    return {
      label: "In Stock",
      className: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
    };
  }
  if (status === "reserved") {
    return {
      label: "Reserved",
      className: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    };
  }
  return {
    label: "Sold",
    className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  };
}

function SummaryRow({
  label,
  value,
  emphasis = false,
  borderTop = false,
}: {
  label: string;
  value: ReactNode;
  emphasis?: boolean;
  borderTop?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-start justify-between gap-2 py-1.5 text-sm",
        borderTop && "mt-0.5 border-t border-border/40 pt-2",
      )}
    >
      <span
        className={cn(
          "min-w-0 flex-1 break-words leading-snug text-muted-foreground",
          emphasis && "font-medium text-foreground",
        )}
      >
        {label}
      </span>
      <span className="max-w-[58%] shrink-0 text-right leading-snug">{value}</span>
    </div>
  );
}

function SectionCard({
  title,
  subtitle,
  badge,
  icon: Icon,
  children,
  action,
  className,
  bodyClassName,
}: {
  title: string;
  subtitle?: ReactNode;
  badge?: ReactNode;
  icon?: typeof Wallet;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("glass-card h-full flex flex-col overflow-hidden", className)}>
      <div className="flex flex-col gap-2.5 border-b border-border/30 bg-muted/15 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4 lg:px-5 lg:py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {Icon ? (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary">
              <Icon className="h-4 w-4" />
            </div>
          ) : null}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="whitespace-nowrap text-sm font-bold text-foreground lg:text-base">{title}</h2>
              {badge}
            </div>
            {subtitle ? <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p> : null}
          </div>
        </div>
        {action ? <div className="min-w-0 w-full sm:w-auto shrink-0">{action}</div> : null}
      </div>
      <div className={cn("min-w-0 p-3 sm:p-4 lg:p-5 flex-1 flex flex-col justify-between", bodyClassName)}>
        {children}
      </div>
    </section>
  );
}

/* ─── Main Component ──────────────────────────────────────────────── */

const SaleReturnProcess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useState(() => getStoredAuthUser());
  // Sale return from product-sales uses sale view; sale_return_can_view_any is only for the Sale Returns/Cancel menu.
  const canView =
    can(user, "sale_can_view") ||
    can(user, "sale_can_view_any") ||
    can(user, "sale_return_can_view_any");
  const canProcess = can(user, "sale_can_update") || can(user, "sale_can_create");

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [sale, setSale] = useState<SaleInvoiceRecord | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [returnDate, setReturnDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [retainedAmount, setRetainedAmount] = useState(0);
  const [refundLines, setRefundLines] = useState<SalePaymentLine[]>([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!id || !canView) return;
    let mounted = true;
    setLoading(true);
    setForbidden(false);
    void fetchSaleReturnInvoice(id)
      .then((row) => {
        if (!mounted) return;
        setSale(row);
        const collected = Number(row?.sale_return?.collected_amount ?? row?.paid_total ?? 0);
        const alreadyRetained = Number(row?.sale_return?.retained_amount ?? 0);
        setRetainedAmount(alreadyRetained);
        const refundDue = Math.max(0, round2(collected - alreadyRetained));
        if (refundDue > 0.009 && !(row?.is_returned || row?.void_type === "return")) {
          const seed = newSalePaymentLine(refundDue);
          if (row?.payment_method) {
            seed.paymentMethod = row.payment_method as SalePaymentLine["paymentMethod"];
          }
          setRefundLines([seed]);
        } else {
          setRefundLines([]);
        }
        if (row?.sale_return?.notes) {
          setNotes(row.sale_return.notes);
        }
      })
      .catch((err) => {
        if (!mounted) return;
        if (isAxiosError(err) && err.response?.status === 403) {
          setForbidden(true);
          return;
        }
        toast.error(saleInvoiceErrorMessage(err));
        setSale(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [id, canView]);

  const isReturned = Boolean(
    sale?.is_returned || sale?.void_type === "return" || sale?.payment_status === "returned",
  );
  const isCancelled = Boolean(
    sale?.is_cancelled || sale?.cancelled_at || sale?.payment_status === "cancelled" || isReturned,
  );

  const collectedAmount = useMemo(() => {
    if (!sale) return 0;
    if (sale.sale_return) return round2(Number(sale.sale_return.collected_amount ?? 0));
    // Gross collections before refund (ignore refund credits for process form).
    const payments = sale.payments ?? [];
    const gross = payments
      .filter((p) => {
        const type = String(p.transaction_type ?? "").toLowerCase();
        return type !== "refund" && Number(p.debit ?? p.amount ?? 0) > 0.009;
      })
      .reduce((sum, p) => sum + Number(p.debit ?? p.amount ?? 0), 0);
    if (gross > 0.009) return round2(gross);
    return round2(Number(sale.paid_total ?? 0));
  }, [sale]);

  const retainedClamped = Math.min(Math.max(0, round2(retainedAmount)), collectedAmount);
  const refundAmount = round2(Math.max(0, collectedAmount - retainedClamped));
  const refundLinesTotal = useMemo(() => salePaymentLinesTotal(refundLines), [refundLines]);
  const refundsMatch =
    refundAmount <= 0.009 || Math.abs(refundLinesTotal - refundAmount) < 0.01;
  const productImageUrl = useMemo(
    () => resolveProductThumbnailUrl(sale?.product ?? null),
    [sale?.product],
  );

  const paymentsIn = useMemo(
    () =>
      (sale?.payments ?? []).filter((p) => {
        const type = String(p.transaction_type ?? "");
        return type !== "refund" && Number(p.debit ?? p.amount ?? 0) > 0.009;
      }),
    [sale?.payments],
  );

  const syncRefundSeed = (nextRetained: number) => {
    const due = round2(Math.max(0, collectedAmount - Math.min(Math.max(0, nextRetained), collectedAmount)));
    if (due <= 0.009) {
      setRefundLines([]);
      return;
    }
    setRefundLines((prev) => {
      if (prev.length === 1) {
        return [{ ...prev[0], amount: due }];
      }
      if (prev.length === 0) {
        const seed = newSalePaymentLine(due);
        if (sale?.payment_method) {
          seed.paymentMethod = sale.payment_method as SalePaymentLine["paymentMethod"];
        }
        return [seed];
      }
      return prev;
    });
  };

  const openConfirm = () => {
    if (retainedClamped - collectedAmount > 0.009) {
      toast.error("Retained amount cannot exceed collected amount.");
      return;
    }
    if (refundAmount > 0.009) {
      if (refundLines.length === 0) {
        toast.error("Add at least one refund method.");
        return;
      }
      if (!refundsMatch) {
        toast.error(
          `Refund methods must total ${formatCurrency(refundAmount)} (currently ${formatCurrency(refundLinesTotal)}).`,
        );
        return;
      }
    }
    setConfirmOpen(true);
  };

  const submitReturn = async () => {
    if (!sale) return;
    setSaving(true);
    try {
      const refunds = buildSalePaymentsPayload(refundLines).map((line) => ({
        amount: line.amount,
        payment_method: line.payment_method,
        bank_id: line.bank_id,
        transaction_reference_no: line.transaction_reference_no,
        pay_order_no: line.pay_order_no,
        notes: line.notes,
      }));
      const primary = refunds[0];
      const r = await processSaleReturn(sale.id, {
        return_date: returnDate,
        notes: notes.trim() || "Sale return — vehicle restored to stock",
        retained_amount: retainedClamped,
        refunds: refundAmount > 0.009 ? refunds : undefined,
        payment_method: primary?.payment_method ?? null,
        bank_id: primary?.bank_id ?? null,
        transaction_reference_no: primary?.transaction_reference_no ?? null,
        pay_order_no: primary?.pay_order_no ?? null,
      });
      toast.success(r.message);
      if (r.record) setSale(r.record);
      setConfirmOpen(false);
      navigate("/sale-returns");
    } catch (e) {
      toast.error(saleInvoiceErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (!canView || forbidden) {
    return (
      <Layout title="Sale Return">
        <PermissionDenied />
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Sale Return">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sale return…
        </div>
      </Layout>
    );
  }

  if (!sale) {
    return (
      <Layout title="Sale Return">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Sale not found.</div>
          <Link to="/sale-returns">
            <Button variant="glass" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to sale returns
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const invoiceNo = invoiceLabel(sale);
  const currSymbol = getActiveCompanyCurrency().symbol;
  const totalSaleAmount = Number(sale.total_amount ?? 0);
  const paidTotalAmount = collectedAmount;
  const displayRetained = isReturned
    ? round2(Number(sale.sale_return?.retained_amount ?? retainedClamped))
    : retainedClamped;
  const displayRefunded = isReturned
    ? round2(Number(sale.sale_return?.refunded_amount ?? refundAmount))
    : refundAmount;
  const vehicleBadge = vehicleStatusBadge(sale);

  return (
    <Layout title={`Sale Return ${invoiceNo}`}>
      <div className="w-full space-y-2 lg:space-y-2">
        {/* ── 1. HEADER BAR ───────────────────────────────────────── */}
        <div className="glass-card flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between lg:gap-3">
          <div className="min-w-0 space-y-1 lg:flex-1">
            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-300">
                <Undo2 className="h-5 w-5" />
              </div>
              <h1 className="font-display text-xl font-bold text-foreground sm:text-2xl">
                Sale Return #{invoiceNo}
              </h1>
              <PaymentStatusBadge status={sale.payment_status} />
              <span className="rounded-md border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold text-foreground">
                {isReturned ? "Already returned" : "Return processing"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground pl-0 sm:pl-13">
              Original sale on{" "}
              {sale.date && isValid(parseISO(sale.date))
                ? format(parseISO(sale.date), "MMMM do, yyyy")
                : (sale.date ?? "—")}
            </p>
          </div>

          <div className="flex flex-nowrap items-center gap-2 shrink-0 overflow-x-auto">
            <Link to="/sale-returns" className="inline-flex shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 h-9 whitespace-nowrap text-xs border-border/60 bg-background text-foreground hover:bg-muted"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            </Link>
            <Link to={`/product-sales/${sale.id}`} className="inline-flex shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 h-9 whitespace-nowrap text-xs border-border/60 bg-background text-foreground hover:bg-muted"
              >
                <FileText className="h-3.5 w-3.5" />
                Open invoice
              </Button>
            </Link>
            <a href={`/sales/${sale.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex shrink-0">
              <Button
                variant="default"
                size="sm"
                className="gap-1.5 h-9 whitespace-nowrap text-xs px-4 font-semibold bg-sky-600 hover:bg-sky-700 text-white shadow-xs"
              >
                <Download className="h-3.5 w-3.5" />
                Download PDF
              </Button>
            </a>
            {!isCancelled && canProcess ? (
              <Button
                type="button"
                variant="premium"
                size="sm"
                className="shrink-0 gap-1.5 h-9 whitespace-nowrap text-xs font-semibold shadow-xs"
                onClick={openConfirm}
                disabled={refundAmount > 0.009 && !refundsMatch}
              >
                <Undo2 className="h-3.5 w-3.5" />
                Process return
              </Button>
            ) : null}
          </div>
        </div>

        {/* ── 2. REFUND SUMMARY BANNER ────────────────────────────── */}
        <div className="glass-card overflow-hidden">
          <div className="px-4 py-4 sm:px-5 lg:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-violet-500/25 bg-violet-500/10 text-violet-700 dark:text-violet-300">
                  <RotateCcw className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    {isReturned ? "Refund Completed" : "Refund to Customer"}
                  </p>
                  <div className="mt-0.5 flex items-baseline gap-2">
                    <span className="text-lg font-bold text-foreground tabular-nums">
                      {currSymbol} {formatAmount(displayRefunded)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      retained {currSymbol} {formatAmount(displayRetained)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5 sm:text-right">
                <div className="flex items-center justify-between gap-6 text-xs font-semibold sm:justify-end">
                  <span className="text-muted-foreground">Collected on sale</span>
                  <span className="text-violet-700 dark:text-violet-300 font-bold">
                    {currSymbol} {formatAmount(paidTotalAmount)}
                  </span>
                </div>
                <div className="h-2.5 w-full sm:w-64 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      "h-full transition-all duration-700 rounded-full",
                      isReturned
                        ? "bg-violet-500"
                        : refundAmount > 0
                          ? "bg-gradient-to-r from-violet-500/70 to-violet-600"
                          : "bg-muted-foreground/30",
                    )}
                    style={{
                      width: `${totalSaleAmount > 0 ? Math.min(100, Math.round((paidTotalAmount / totalSaleAmount) * 100)) : 0}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── 3. TWO-COLUMN: SALE OVERVIEW + RETURN FINANCIALS ── */}
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          <SectionCard
            title="Sale Overview"
            icon={FileText}
            action={
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-muted/40 px-2.5 py-1 text-xs font-mono font-bold text-foreground shadow-2xs">
                Ref #{invoiceNo}
              </span>
            }
          >
            <div className="space-y-3.5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card/60 p-3.5 shadow-2xs hover:border-border transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-blue-500/20 bg-blue-500/10 text-blue-600 dark:text-blue-400">
                      <User className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Customer
                      </p>
                      <p className="mt-0.5 truncate text-sm font-bold text-foreground">
                        {sale.customer?.name ?? "Walk-in Customer"}
                      </p>
                      {sale.customer?.phone ? (
                        <a
                          href={`tel:${sale.customer.phone}`}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                        >
                          <Phone className="h-3 w-3 shrink-0" />
                          <span>{sale.customer.phone}</span>
                        </a>
                      ) : (
                        <p className="mt-1 text-[11px] text-muted-foreground">No phone provided</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card/60 p-3.5 shadow-2xs hover:border-border transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-purple-500/20 bg-purple-500/10 text-purple-600 dark:text-purple-400">
                      <UserCheck className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Salesperson
                      </p>
                      {sale.employee?.full_name ? (
                        <>
                          <p className="mt-0.5 truncate text-sm font-bold text-foreground">
                            {sale.employee.full_name}
                          </p>
                          {sale.employee.employee_code ? (
                            <p className="mt-1 text-[11px] font-medium text-muted-foreground">
                              Code:{" "}
                              <span className="font-mono text-foreground/80">
                                {sale.employee.employee_code}
                              </span>
                            </p>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <p className="mt-0.5 text-sm font-medium text-foreground/80">
                            Direct Showroom Sale
                          </p>
                          <p className="mt-1 text-[11px] text-muted-foreground">No agent assigned</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {sale.description?.trim() ? (
                <div className="rounded-xl border border-border/40 bg-muted/20 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Notes &amp; Remarks
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-xs text-foreground/90 leading-relaxed">
                    {sale.description}
                  </p>
                </div>
              ) : null}

              <div className="relative overflow-hidden rounded-xl border border-border/60 bg-gradient-to-br from-card to-muted/20 p-3.5 shadow-2xs hover:border-primary/40 transition-colors">
                <div className="flex items-center gap-3.5">
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-muted shadow-2xs relative group">
                    <ProductImageThumb
                      src={productImageUrl}
                      alt={sale.product?.name ?? "Product"}
                      iconClassName="h-7 w-7"
                    />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-primary flex items-center gap-1">
                        <Car className="h-3 w-3" />
                        Vehicle
                      </span>
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[10px] font-semibold border",
                          vehicleBadge.className,
                        )}
                      >
                        {vehicleBadge.label}
                      </span>
                    </div>
                    {sale.product?.id ? (
                      <Link
                        to={`/products/${sale.product.id}`}
                        className="group/link flex items-center gap-1.5 truncate text-sm font-bold text-foreground hover:text-primary transition-colors"
                      >
                        <span className="truncate">{sale.product?.name ?? "—"}</span>
                        <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity text-primary" />
                      </Link>
                    ) : (
                      <p className="truncate text-sm font-bold text-foreground">
                        {sale.product?.name ?? "—"}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right pl-3 border-l border-border/40">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Sale Total
                    </p>
                    <MoneyAmount
                      value={sale.total_amount}
                      emphasis
                      showShort
                      amountClassName="text-base font-extrabold"
                    />
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            title="Return Financials"
            icon={Wallet}
            action={
              <div className="flex flex-wrap items-center gap-1.5 justify-end">
                <PaymentStatusBadge status={sale.payment_status} />
              </div>
            }
          >
            <div className="space-y-1.5">
              <SummaryRow
                label="Total Sale Price"
                value={<MoneyAmount value={sale.total_amount} emphasis />}
              />
              <SummaryRow
                label="Collected from Customer"
                value={
                  <MoneyAmount
                    value={paidTotalAmount}
                    amountClassName="text-emerald-600 dark:text-emerald-400 font-semibold"
                  />
                }
              />
              <SummaryRow
                label="Retained by Showroom"
                value={
                  <MoneyAmount
                    value={displayRetained}
                    amountClassName="text-amber-700 dark:text-amber-400 font-semibold"
                  />
                }
              />
              <SummaryRow
                label={isReturned ? "Refunded to Customer" : "Refund to Customer"}
                value={
                  <MoneyAmount
                    value={displayRefunded}
                    amountClassName="text-violet-700 dark:text-violet-300 font-semibold"
                  />
                }
                emphasis
              />

              <div className="rounded-xl border border-border/40 bg-muted/15 px-3 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  What this return will do
                </p>
                <ul className="space-y-1.5 text-xs text-foreground/90">
                  <li>• Keep original sale collections and journals for audit</li>
                  <li>• Reverse sale revenue / COGS (and restore inventory)</li>
                  <li>
                    • Refund {formatCurrency(displayRefunded)} to the customer
                  </li>
                  <li>
                    • Recognize {formatCurrency(displayRetained)} as showroom return-fee income
                  </li>
                  <li>
                    • Restore the vehicle to <strong>in stock</strong>
                  </li>
                </ul>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* ── 4. COLLECTIONS LEDGER ────────────────────────────────── */}
        {paymentsIn.length > 0 ? (
          <div className="glass-card overflow-hidden p-5 sm:p-6 space-y-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-blue-500/20 bg-blue-500/10 text-blue-600 dark:text-blue-400 mt-0.5">
                  <History className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h2 className="text-base font-bold text-foreground sm:text-lg">
                      Collections on this Sale
                    </h2>
                    <span className="rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                      {paymentsIn.length} {paymentsIn.length === 1 ? "Entry" : "Entries"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Original collections kept for audit; only the refund amount leaves cash
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-right">
                <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
                  TOTAL COLLECTED
                </p>
                <span className="text-lg font-extrabold text-foreground tabular-nums">
                  {currSymbol} {formatAmount(paidTotalAmount)}
                </span>
              </div>
            </div>

            <div className="-mx-5 overflow-x-auto sm:-mx-6 border-t border-border/40 pt-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <th className="px-5 py-3 text-left sm:px-6">Date</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Method</th>
                    <th className="px-5 py-3 text-right sm:px-6">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/25">
                  {paymentsIn.map((p) => {
                    const date = p.payment_date ? parseISO(p.payment_date) : null;
                    return (
                      <tr key={p.id} className="hover:bg-muted/15 transition-colors">
                        <td className="px-5 py-2.5 whitespace-nowrap sm:px-6 text-xs font-bold text-foreground">
                          {date && isValid(date)
                            ? format(date, "MMM d, yyyy")
                            : (p.payment_date ?? "—")}
                        </td>
                        <td className="px-4 py-2.5 text-xs font-semibold text-foreground">
                          {String(p.transaction_type_label || p.transaction_type || "Payment")}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-muted-foreground capitalize">
                          {p.payment_method_label ?? p.payment_method ?? "—"}
                        </td>
                        <td className="px-5 py-2.5 text-right sm:px-6">
                          <span className="text-sm font-extrabold text-foreground tabular-nums">
                            {formatCurrency(Number(p.debit ?? p.amount ?? 0))}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {/* ── 5. PROCESS RETURN FORM ──────────────────────────────── */}
        {!isCancelled && canProcess ? (
          <SectionCard
            title="Process Return"
            subtitle="Set retained fee, return date, refund methods, then confirm"
            icon={Undo2}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="return-date">Return date</Label>
                  <DatePartsPicker
                    id="return-date"
                    value={returnDate}
                    onChange={setReturnDate}
                    disabled={saving}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="retained-amount">Amount retained by showroom</Label>
                  <Input
                    id="retained-amount"
                    type="number"
                    min={0}
                    step="0.01"
                    max={collectedAmount}
                    className="glass-input h-10"
                    value={Number.isFinite(retainedAmount) ? retainedAmount : 0}
                    onChange={(e) => {
                      const next = round2(Number(e.target.value || 0));
                      setRetainedAmount(next);
                      syncRefundSeed(next);
                    }}
                    disabled={saving}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Refund due: <span className="font-semibold text-foreground">{formatCurrency(refundAmount)}</span>
                    {" · "}Collected {formatCurrency(collectedAmount)}
                  </p>
                </div>
              </div>

              {refundAmount > 0.009 ? (
                <SalePaymentLinesSection
                  lines={refundLines}
                  onChange={setRefundLines}
                  expectedTotal={refundAmount}
                  salePrice={refundAmount}
                  title="Refund methods"
                  addButtonLabel="Add refund method"
                  emptyTitle="No refund methods added."
                  emptyHint='Click "Add refund method" to split the refund across cash, online, bank, cheque, etc.'
                  removeDescription="This refund method line will be removed."
                />
              ) : collectedAmount > 0.009 ? (
                <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-2.5">
                  Full collected amount is retained as showroom fee — no customer refund is required.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-2.5">
                  No money was collected on this sale, so no refund methods are required.
                </p>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="return-notes">Notes / reason (optional)</Label>
                <Textarea
                  id="return-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reason for return…"
                  rows={3}
                />
              </div>

              <div className="flex justify-end pt-1">
                <Button
                  type="button"
                  variant="premium"
                  className="gap-1.5 h-10 px-5 text-xs font-semibold shadow-xs"
                  onClick={openConfirm}
                  disabled={refundAmount > 0.009 && !refundsMatch}
                >
                  <Undo2 className="h-4 w-4" />
                  Process sale return
                </Button>
              </div>
            </div>
          </SectionCard>
        ) : null}

        {/* ── RETURN HISTORY (already returned) ───────────────────── */}
        {isReturned && sale.sale_return ? (
          <SectionCard title="Sale Return History" icon={History}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-border/50 bg-muted/10 px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Sale</p>
                <p className="mt-1 text-sm font-bold tabular-nums">{formatCurrency(sale.sale_return.sale_amount)}</p>
              </div>
              <div className="rounded-lg border border-border/50 bg-muted/10 px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Collected</p>
                <p className="mt-1 text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                  {formatCurrency(sale.sale_return.collected_amount)}
                </p>
              </div>
              <div className="rounded-lg border border-border/50 bg-muted/10 px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Refunded</p>
                <p className="mt-1 text-sm font-bold tabular-nums text-violet-700 dark:text-violet-300">
                  {formatCurrency(sale.sale_return.refunded_amount)}
                </p>
              </div>
              <div className="rounded-lg border border-border/50 bg-muted/10 px-3 py-2.5">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Retained</p>
                <p className="mt-1 text-sm font-bold tabular-nums text-amber-700 dark:text-amber-400">
                  {formatCurrency(sale.sale_return.retained_amount)}
                </p>
              </div>
            </div>
            {sale.sale_return.notes ? (
              <p className="mt-3 text-xs text-muted-foreground">
                Notes: <span className="text-foreground">{sale.sale_return.notes}</span>
              </p>
            ) : null}
            {sale.sale_return.refund_payments && sale.sale_return.refund_payments.length > 0 ? (
              <div className="mt-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Refund methods
                </p>
                <ul className="space-y-1.5 rounded-lg border border-border/50 bg-muted/10 px-3 py-2">
                  {sale.sale_return.refund_payments.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-3 text-xs">
                      <span className="text-muted-foreground min-w-0 truncate">
                        {p.payment_date ?? "—"} · {p.payment_method_label ?? p.payment_method ?? "—"}
                        {p.bank_name ? ` · ${p.bank_name}` : ""}
                      </span>
                      <span className="font-semibold tabular-nums shrink-0">{formatCurrency(p.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </SectionCard>
        ) : null}

        {/* ── RETURNED / CANCELLED BANNER ─────────────────────────── */}
        {isCancelled ? (
          <div
            className={cn(
              "rounded-xl border px-4 py-3 text-sm",
              isReturned
                ? "border-violet-500/30 bg-violet-500/10 text-violet-900 dark:text-violet-200"
                : "border-rose-500/30 bg-rose-500/10 text-rose-800 dark:text-rose-200",
            )}
          >
            {isReturned
              ? `This sale was returned. Refunded ${formatCurrency(displayRefunded)}; retained ${formatCurrency(displayRetained)} as showroom fee; vehicle restored to stock.`
              : "This sale is already cancelled and cannot be returned again."}
            {sale.cancel_notes ? (
              <span className="mt-1 block text-xs opacity-90">{sale.cancel_notes}</span>
            ) : null}
          </div>
        ) : null}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(open) => !saving && setConfirmOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Process this sale return?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This reverses sale #{invoiceNo}, refunds{" "}
                <strong>{formatCurrency(refundAmount)}</strong>
                {refundLines.length > 1 ? ` across ${refundLines.length} methods` : ""} to the
                customer
                {retainedClamped > 0.009 ? (
                  <>
                    , retains <strong>{formatCurrency(retainedClamped)}</strong> as showroom fee
                  </>
                ) : null}
                , and restores <strong>{sale.product?.name || "the vehicle"}</strong> to stock.
              </span>
              <span className="block text-xs text-muted-foreground">
                Original collections and journals stay for audit. Partner/owner payouts already paid are not auto-reversed.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Keep sale</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              className="bg-violet-600 text-white hover:bg-violet-700"
              onClick={(e) => {
                e.preventDefault();
                void submitReturn();
              }}
            >
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Confirm return &amp; refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default SaleReturnProcess;
